import * as React from "react";
import Logo from "src/components/Logo";
import { OrderProps } from "src/resources/sales/interface";
import { useTheme } from "@mui/material/styles";
import {
    DateField
  } from "react-admin";
import {
    Box,
    Table,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Card,
    Typography,
    Grid
  } from "@mui/material";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import HomeIcon from "@mui/icons-material/Home";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import BusinessIcon from "@mui/icons-material/Business";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import { phoneNumberFormat, priceFormat } from "src/common/UtilFunctions";
import { OwnAccountShippingMethodServiceEnum, OwnAccountEnum, ShippingMethodEnum, CourierEnum } from "src/common/enum";

interface PackingSlipProps {
  record: OrderProps;
}

export const PackingSlip = (props: PackingSlipProps) => {
  const { record } = props

  const theme = useTheme();

  const cardStyle = {
    padding: "10px",
    border: "1px solid #ebebeb",
    boxShadow: "none",
    borderRadius: "0px"
  };

  const sectionStyle = {
    margin: "5px 0 5px 15px",
    alignItems: "center",
    display: "flex",
  };

  const iconStyle = {
    marginRight: "10px",
    color: theme.palette.grey["700"],
    fontSize: "16px",
  };

  const textStyle = { fontSize: "14px", wordBreak: "break-word" };

  return (
    <Box sx={{paddingTop: "32px"}}>
        <Grid container spacing={4}>
            <Grid item sm={8} md={8}>
                <Typography variant="h5" gutterBottom>Packing Slip</Typography>
                <Typography variant="subtitle2">Order Date: <DateField showTime={true} source="created_at" /></Typography>
                <Typography variant="subtitle2">Order #{record.increment_id}</Typography>
                {record.note &&
                    <Typography variant="body2"><b>Note:</b> {record.note}</Typography>
                }
            </Grid>
            <Grid item sm={4} md={4} sx={{display: "block"}}>
                <Box sx={{paddingTop: "10px", paddingBottom: "10px"}}>
                    <Logo color="#000"/>
                </Box>
                <Box>
                    <Typography variant="subtitle2">979 Third Avenue, Suite 715</Typography>
                    <Typography variant="subtitle2">New York, NY 10022</Typography>
                    <Typography variant="subtitle2" sx={{display: "flex", alignItems: "center"}}><LocalPhoneIcon sx={iconStyle} />(212) 758-9900</Typography>
                    <Typography variant="subtitle2" sx={{display: "flex", alignItems: "center"}}><AlternateEmailIcon sx={iconStyle} />castel@castelmaison.com</Typography>
                </Box>
            </Grid>
            <Grid item sm={4} md={4}>
                <Typography variant="h6" gutterBottom>Sold To {record.user && `#${record?.user?.id}`} {record.sales_tax && `Resale#${record?.sales_tax}`}</Typography>
                <Card sx={cardStyle}>
                    <Box display="flex" alignItems="center" sx={sectionStyle}>
                        <BusinessIcon sx={iconStyle} />
                        <Box>
                        <Typography
                            sx={textStyle}
                            fontWeight="fontWeightMedium"
                        >{`${record.billing_address.first_name} ${record.billing_address.last_name}`}</Typography>
                        <Typography
                            sx={textStyle}
                        >{`${record.billing_address.company}`}</Typography>
                        </Box>
                    </Box>

                    <Box display="flex" alignItems="center" sx={sectionStyle}>
                        <HomeIcon sx={iconStyle} />
                        <Box>
                        <Typography
                            sx={textStyle}
                        >{`${record.billing_address.street_address}, ${record.billing_address.city} ${record.billing_address.zip}`}</Typography>
                        <Typography
                            sx={textStyle}
                        >{`${record.billing_address.state}, ${record.billing_address.country}`}</Typography>
                        </Box>
                    </Box>

                    <Box display="flex" alignItems="center" sx={sectionStyle}>
                        <LocalPhoneIcon sx={iconStyle} />
                        <Box>
                        <Typography sx={textStyle}>{phoneNumberFormat(record.billing_address.phone)}</Typography>
                        <Typography sx={textStyle}>{phoneNumberFormat(record.billing_address.mobile)}</Typography>
                        </Box>
                    </Box>

                    <Box display="flex" sx={sectionStyle}>
                        <AlternateEmailIcon sx={iconStyle} />
                        <Typography sx={textStyle}>{record.billing_address.email}</Typography>
                    </Box>

                    {record.billing_address.note &&
                    <Box display="block" sx={{...sectionStyle, marginTop: "20px"}}>
                        <Typography sx={{...textStyle, fontWeight: "500"}}>Note:</Typography>
                        <Typography sx={textStyle}>{record.billing_address.note}</Typography>
                    </Box> 
                    }
                </Card>
            </Grid>

            <Grid item sm={4} md={4}>
                <Typography variant="h6" gutterBottom>Shipped To</Typography>
                <Card sx={cardStyle}>
                    <Box display="flex" alignItems="center" sx={sectionStyle}>
                        <BusinessIcon sx={iconStyle} />
                        <Box>
                        <Typography
                            sx={textStyle}
                            fontWeight="fontWeightMedium"
                        >{`${record.shipping_address.first_name} ${record.shipping_address.last_name}`}</Typography>
                        <Typography
                            sx={textStyle}
                        >{`${record.shipping_address.company}`}</Typography>
                        </Box>
                    </Box>

                    <Box display="flex" alignItems="center" sx={sectionStyle}>
                        <HomeIcon sx={iconStyle} />
                        <Box>
                        <Typography
                            sx={textStyle}
                        >{`${record.shipping_address.street_address}, ${record.shipping_address.city} ${record.shipping_address.zip}`}</Typography>
                        <Typography
                            sx={textStyle}
                        >{`${record.shipping_address.state}, ${record.shipping_address.country}`}</Typography>
                        </Box>
                    </Box>

                    <Box display="flex" alignItems="center" sx={sectionStyle}>
                        <LocalPhoneIcon sx={iconStyle} />
                        <Box>
                        <Typography sx={textStyle}>{phoneNumberFormat(record.shipping_address.phone)}</Typography>
                        <Typography sx={textStyle}>{phoneNumberFormat(record.shipping_address.mobile)}</Typography>
                        </Box>
                    </Box>

                    <Box display="flex" sx={sectionStyle}>
                        <AlternateEmailIcon sx={iconStyle} />
                        <Typography sx={textStyle}>{record.shipping_address.email}</Typography>
                    </Box>

                    {record.shipping_address.note &&
                    <Box display="block" sx={{...sectionStyle, marginTop: "20px"}}>
                        <Typography sx={{...textStyle, fontWeight: "500", paddingRight: "10px"}}>Note:</Typography>
                        <Typography sx={textStyle}>{record.shipping_address.note}</Typography>
                    </Box> 
                    }
                </Card>
            </Grid>

            <Grid item sm={4} md={4}>
                <Typography variant="h6" gutterBottom>{`Shipping & Handling`}</Typography>
                <Card sx={cardStyle}>
                    <Box display="flex" alignItems="center" sx={sectionStyle}>
                        <LocalShippingIcon sx={iconStyle} />
                        <Box>
                        <Typography
                            sx={{...textStyle, marginTop: "-2px"}}
                            fontWeight="fontWeightMedium"
                        >{`${ShippingMethodEnum.find((method) => method.id === record.shipping_method?.type)?.name}`}</Typography>
                        </Box>
                    </Box>
                    {record.shipping_method?.additional ? (
                        <>
                        <Box display="flex" alignItems="center" sx={sectionStyle}>
                            <Typography sx={textStyle}>
                            {`${
                            record.shipping_method?.additional?.courier
                                ? CourierEnum.find((method) => method.id === record.shipping_method?.additional?.courier)?.name
                                : ""
                            } (${OwnAccountShippingMethodServiceEnum.find((method) => method.id === record.shipping_method?.additional?.service)?.name})`}
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" sx={sectionStyle}>
                            <Typography sx={textStyle}>
                            {`${
                            record.shipping_method?.additional?.account
                                ? OwnAccountEnum.find((method) => method.id === record.shipping_method?.additional?.account)?.name
                                : ""
                            }`}
                            </Typography>
                            <Typography sx={{...textStyle, paddingLeft: "5px"}}>
                            {`${
                                record.shipping_method?.additional
                                ? `#${record.shipping_method?.additional?.account_number}`
                                : ""
                            }`}
                            </Typography>
                        </Box>
                        </>
                    ) : null}
                </Card>
            </Grid>

            <Grid item sm={12} md={12}>
                <Typography variant="h6" gutterBottom>Items</Typography>            
                <TableContainer sx={{
                    border: "1px solid #ebebeb", 
                    boxShadow: "none",
                    boxSizing: "border-box",
                    borderBottom: "none",
                    borderTop: "1px solid #ebebeb"
                }}>
                    <Table size="small">
                        <TableHead>
                        <TableRow>
                            <TableCell sx={{fontWeight: "600"}}>
                            Name
                            </TableCell>
                            <TableCell sx={{fontWeight: "600"}}>
                            SKU
                            </TableCell>
                            <TableCell sx={{fontWeight: "600"}}>
                            Sidemark
                            </TableCell>
                            <TableCell sx={{fontWeight: "600"}}>
                            Description
                            </TableCell>
                            <TableCell sx={{fontWeight: "600"}}>
                            Quantity{record.type === 'fabric' && ' (Yards)'}
                            </TableCell>
                        </TableRow>
                        </TableHead>

                        <TableBody>
                        { record.items.sort((a, b) => a.product?.sku?.localeCompare(b.product?.sku)).map((orderItem, orderItemIndex) => {
                            let productParent = orderItem.product.parent ?? orderItem.product;

                            return (
                                <TableRow key={`row_${orderItemIndex}`} >
                                    <TableCell>
                                        <Typography variant="body2" component="div" sx={{display: "flex", flexWrap: "nowrap", alignItems: "center"}}>
                                            {`${productParent.name} / ${orderItem.product.name}`}
                                            {orderItem.group_items?.length > 0 &&
                                            <Typography variant="body2" component="div" sx={{marginLeft: "5px", display: "flex", flexWrap: "nowrap", alignItems: "center"}}>
                                                <strong>Ringset</strong> ({orderItem.group_items.map((item) => item.sku).join(', ')})
                                            </Typography>      
                                            }
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {orderItem.product.sku}
                                    </TableCell>
                                    <TableCell>
                                        {orderItem.note}
                                    </TableCell>
                                    <TableCell>
                                        {record.type === 'sample' ? 'Sample' : productParent.attributes.content}
                                    </TableCell>
                                    <TableCell>
                                        {orderItem.quantity}
                                    </TableCell>
                                </TableRow>
                            )   
                        })}
                        </TableBody>
                    </Table>
                </TableContainer>

            </Grid>
        </Grid>
        <Grid item sm={12} md={12}>
            <Card sx={{...cardStyle, marginTop:"20px"}}>
                {record.type === 'sample' ?
                    <Typography variant="body2" sx={{display: "flex", flexWrap: "nowrap", alignItems: "center", fontWeight: "600"}}>
                        IF YOU HAVE ANY QUESTIONS, PLEASE E-MAIL sampling@castelmaison.com
                    </Typography>
                :
                    <Typography variant="body2" sx={{display: "flex", flexWrap: "nowrap", alignItems: "center", fontWeight: "600"}}>
                        PLEASE INSPECT GOOD BEFORE CUTTING<br/>
                        NO CLAIMS WILL BE CONSIDERED IF GOODS HAVE BEEN CUT<br/>
                        IF YOU HAVE ANY QUESTIONS, PLEASE CALL CASTEL @ 212 758 9900
                    </Typography>
                }
                {/**
                * TODO: call config phone
                */}
            </Card>
        </Grid>
        {record.type === 'fabric' &&
            <Grid item sm={12} md={12}>
                <Card sx={{...cardStyle, marginTop:"20px"}}>
                    <Typography variant="caption" sx={{display: "flex", flexWrap: "nowrap", alignItems: "center", fontWeight: "500"}}>
                        Merchandise must be inspected within 10 days of delivery. No claims will be honored after 10 days or if merchandise has been cut. No claims are allowed for labor charges. All orders are subject to our terms & conditions which are available on our website: www.castelmaison.com. All prices are FOB New York. Design ©
                    </Typography>
                </Card>
            </Grid>
        }
    </Box>
  );
};
